import { CommonModule } from "@angular/common";
import {
    afterNextRender,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    inject,
    Injector,
    input,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { S25Durationfilter } from "../../modules/s25-editable/s25-editable-duration/s25.durationfilter.service";
import { S25EditableModule } from "../../modules/s25-editable/s25.editable.module";
import { S25ExpressSchedulingChatbotComponent } from "../../modules/s25-chat-bot/s25.express.scheduling.chat.bot";
import { S25DropdownPaginatedModule } from "../../modules/s25-dropdown/s25.dropdown.paginated.module";
import { S25LoadingInlineStaticComponent } from "../../modules/s25-loading/s25.loading.inline.static.component";
import { S25ToggleButtonModule } from "../../modules/s25-toggle-button/s25.toggle.button.module";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25PipesModule } from "../../pipes/s25.pipes.module";
import { S25Util } from "../../util/s25-util";
import type { Nullable } from "../../pojo/Util";
import type {
    ExpressSchedulingAiSaveHandler,
    ExpressSchedulingInitContext,
    ExpressSchedulingLocationItem,
    ExpressSchedulingPanelApi,
} from "./s25.express.scheduling.types";
import { S25ExpressSchedulingHost } from "./s25.express.scheduling.host";

@TypeManagerDecorator("s25-ng-express-scheduling")
@Component({
    selector: "s25-ng-express-scheduling",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: "s25-express-scheduling" },
    styles: `
        :host {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
        }

        label,
        fieldset.scheduling-field > legend {
            font-weight: bold;
            font-size: 1em !important;
            line-height: inherit;
            margin: 0 0 0.25rem;
            display: block;
            padding: 0;
            float: none;
            width: auto;
            max-width: 100%;
        }

        .express-scheduling--wrapper {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            text-align: left;
        }

        /* Embed iframes can be arbitrarily wide; dash/modal are not .inside-iframe. */
        :host-context(.inside-iframe) .express-scheduling--wrapper {
            max-width: 16rem;
        }

        .scheduling-field {
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            width: 100%;
        }

        fieldset.scheduling-field {
            margin: 0;
            padding: 0;
            border: none;
            min-inline-size: 0;
        }

        .duration-summary {
            display: flex;
            flex-direction: column;
            align-items: center;
            box-sizing: border-box;
            width: 100%;
            margin-top: 0.5rem;
            padding: 0.8em 0;
            text-align: center;
            border: 1px solid var(--color--border);
            background: var(--color--surface-bg);
            color: var(--color--text);
        }

        .duration-summary__value {
            font-weight: bold;
        }

        .scheduling-warning {
            font-weight: bold;
            color: var(--error-message-color);
        }

        .scheduling-warning--past-date {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            gap: 0.5rem;
        }

        s25-ng-editable-start-end-datetime {
            display: block;
            width: 100%;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--wrapper {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            flex-wrap: nowrap;
            gap: 0.5rem;
            width: 100%;
            max-width: none;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--wrapper > div {
            width: 100%;
        }

        /* Empty end-date slot (when not spanning midnight) still occupies gap — drop it. */
        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--wrapper > div:empty {
            display: none;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--wrapper .s25-datepicker s25-alert {
            display: none;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date,
        s25-ng-editable-start-end-datetime ::ng-deep .timepicker.ngInlineBlock {
            display: block !important;
            box-sizing: border-box;
            width: 100%;
            max-width: none;
            vertical-align: middle;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date > div,
        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date > div > div,
        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date .editable,
        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date .editable-dp,
        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date s25-datepicker,
        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date .form-group,
        s25-ng-editable-start-end-datetime ::ng-deep .timepicker .ngTimepicker {
            display: block;
            box-sizing: border-box;
            width: 100%;
            max-width: 100%;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date .editable-dp {
            display: flex;
        }

        s25-ng-editable-start-end-datetime
            ::ng-deep
            .c-start-end-datetime-picker--editable-date
            .editable-dp
            s25-datepicker {
            flex: 1 1 auto;
            min-width: 0;
        }

        s25-ng-editable-start-end-datetime ::ng-deep .c-start-end-datetime-picker--editable-date input.c-input,
        s25-ng-editable-start-end-datetime ::ng-deep .ngTimepickerInput.cn-form__control {
            box-sizing: border-box;
            width: 100% !important;
            max-width: 100% !important;
            height: 2em;
            min-height: 2em;
            margin: 0;
            padding: 0 0.75em !important;
            line-height: 2em;
        }

        s25-location-dropdown {
            display: block;
            width: 100%;
        }

        s25-location-dropdown ::ng-deep .ngDropdownPaginated,
        s25-location-dropdown ::ng-deep .select2-container {
            width: 100%;
        }

        .event-name-input {
            box-sizing: border-box;
            width: 100%;
        }

        .scheduling-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            align-items: center;
        }

        .post-save {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }
    `,
    imports: [
        CommonModule,
        FormsModule,
        S25ToggleButtonModule,
        S25DropdownPaginatedModule,
        S25EditableModule,
        S25ExpressSchedulingChatbotComponent,
        S25LoadingInlineStaticComponent,
        S25PipesModule,
    ],
    template: `
        @if (host(); as schedulingHost) {
            @if (schedulingHost.hasAiFeaturesEnabled) {
                <s25-toggle-button [falseLabel]="'Standard'" [trueLabel]="'AI'" [(value)]="aiModeEnabled" />
            }

            @if (aiModeEnabled()) {
                <s25-ng-ai-express-scheduling-chatbot [onChange]="aiSaveHandler" />
            }

            <div class="express-scheduling--wrapper">
                @if (schedulingHost.init && !schedulingHost.postSavePage) {
                    @if (!aiModeEnabled() || !schedulingHost.hasAiFeaturesEnabled) {
                        <fieldset class="scheduling-field">
                            <legend class="ngBold">Date</legend>
                            @if (expressDateTimeContext(); as dateTime) {
                                <s25-ng-editable-start-end-datetime
                                    [startDatetime]="dateTime.evStartDt"
                                    [endDatetime]="dateTime.evEndDt"
                                    [minDate]="dateTime.minCreateDate"
                                    [maxDate]="dateTime.maxCreateDate"
                                    [noSpansMidnightEdit]="true"
                                    [noTimeUpdated]="true"
                                    [alwaysEditing]="true"
                                    (startDatetimeChange)="onStartDatetimeChange($event)"
                                    (endDatetimeChange)="onEndDatetimeChange($event)"
                                />
                                @if (eventDurationLabel()) {
                                    <div class="duration-summary rose-form--additional-time--summary">
                                        <span>Duration:</span>
                                        <span class="duration-summary__value">{{ eventDurationLabel() }}</span>
                                    </div>
                                }
                            }
                            @if (schedulingHost.pastDateWarning) {
                                <div
                                    class="scheduling-warning scheduling-warning--past-date rose-form--error-text"
                                    tabindex="0"
                                >
                                    {{ schedulingHost.pastDateWarning }}
                                    <button
                                        type="button"
                                        class="aw-button aw-button--outline"
                                        (click)="ignorePastDateWarning()"
                                    >
                                        Ignore
                                    </button>
                                </div>
                            }
                            @if (schedulingHost.durationWarning) {
                                <div class="scheduling-warning">{{ schedulingHost.durationWarning }}</div>
                            }
                            @if (schedulingHost.quotaWarning) {
                                <div class="scheduling-warning">{{ schedulingHost.quotaWarning }}</div>
                            }
                        </fieldset>

                        <div class="scheduling-field scheduling-field--location">
                            <label class="ngBold">Express Locations</label>
                            @if (schedulingHost.hasLocationDropdown) {
                                <s25-location-dropdown
                                    [express]="schedulingHost.locationFilter"
                                    [apiBean]="schedulingHost.locationDropdownApi"
                                    [onLoad]="locationsLoadHandler"
                                    [onSelect]="locationSelectHandler"
                                    [value]="locationSelection()"
                                    (valueChange)="onLocationSelectionChange($event)"
                                    [ariaLabel]="'Express Locations'"
                                />
                            }
                        </div>

                        <div class="scheduling-field">
                            <label class="ngBold" for="express-scheduling-event-name">Event Name</label>
                            <input
                                id="express-scheduling-event-name"
                                class="event-name-input c-input"
                                type="text"
                                [ngModel]="schedulingHost.name"
                                (ngModelChange)="onEventNameChange($event)"
                            />
                        </div>

                        @if (inlineLoading()) {
                            <s25-ng-loading-inline-static />
                        }

                        <div class="scheduling-actions">
                            <button
                                type="button"
                                class="aw-button aw-button--primary"
                                [disabled]="!schedulingHost.canCreateExpress"
                                (click)="saveExpressEvent()"
                            >
                                Save
                            </button>
                            @if (schedulingHost.hasEventForm) {
                                <button
                                    type="button"
                                    class="aw-button aw-button--outline open-in-form-btn"
                                    (click)="openInForm()"
                                >
                                    Open in Form
                                </button>
                            }
                        </div>
                    }
                }

                @if (schedulingHost.postSavePage) {
                    <div class="post-save">
                        @for (notification of schedulingHost.notifications; track $index) {
                            <div class="post-save-notification" [innerHTML]="notification | safeHTML"></div>
                        }
                        <button type="button" class="aw-button aw-button--primary" (click)="exitPostSavePage()">
                            Create Another Event
                        </button>
                    </div>
                }
            </div>
        }
    `,
})
export class S25ExpressSchedulingComponent {
    readonly panelApi = input<ExpressSchedulingPanelApi>();
    readonly initContext = input<ExpressSchedulingInitContext>();

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly injector = inject(Injector);

    /** Undefined only before {@link initialize} completes. */
    readonly host = signal<S25ExpressSchedulingHost | undefined>(undefined);
    readonly aiModeEnabled = signal(false);
    readonly inlineLoading = signal(false);
    /** Mirrors location dropdown value; null means no selection. */
    readonly locationSelection = signal<Nullable<ExpressSchedulingLocationItem>>(null);
    /** Bumped when dateModel time range mutates; host() alone does not invalidate nested reads. */
    private readonly dateTimeRevision = signal(0);

    readonly dateTimeRangeReady = computed(() => {
        this.dateTimeRevision();
        const schedulingHost = this.host();
        const timeModel = schedulingHost?.dateModel?.timeModel;
        return !!(schedulingHost?.init && timeModel?.evStartDt && timeModel.evEndDt);
    });

    readonly expressDateTimeContext = computed(() => {
        this.dateTimeRevision();
        if (!this.dateTimeRangeReady()) {
            return undefined;
        }
        const dateModel = this.host()?.dateModel;
        const evStartDt = dateModel?.timeModel.evStartDt;
        const evEndDt = dateModel?.timeModel.evEndDt;
        if (!evStartDt || !evEndDt) {
            return undefined;
        }
        return {
            evStartDt,
            evEndDt,
            minCreateDate: dateModel.minCreateDate,
            maxCreateDate: dateModel.maxCreateDate,
        };
    });

    readonly eventDurationLabel = computed(() => {
        this.dateTimeRevision();
        const timeModel = this.host()?.dateModel?.timeModel;
        if (!timeModel?.evStartDt || !timeModel.evEndDt) {
            return "";
        }
        const minutes = S25Util.date.diffMinutes(timeModel.evStartDt, timeModel.evEndDt);
        if (minutes <= 0) {
            return "";
        }
        return S25Durationfilter.duration(S25Util.minutesToISODuration(minutes));
    });

    readonly locationsLoadHandler = (): void => {
        void this.host()?.onLocationsLoad();
    };

    readonly locationSelectHandler = (selectedLocation: Nullable<ExpressSchedulingLocationItem>): void => {
        void this.onLocationSelected(selectedLocation);
    };

    readonly aiSaveHandler: ExpressSchedulingAiSaveHandler = (reservationAttemptDetails) =>
        this.host()?.aiSave(reservationAttemptDetails) ?? Promise.resolve();

    constructor() {
        afterNextRender(
            () => {
                void this.initialize();
            },
            { injector: this.injector },
        );

        this.destroyRef.onDestroy(() => {
            this.host()?.markDestroyed();
        });
    }

    onEventNameChange(name: string): void {
        const schedulingHost = this.host();
        if (!schedulingHost) {
            return;
        }
        schedulingHost.name = name;
    }

    ignorePastDateWarning(): void {
        this.host()?.ignorePastDateWarning();
        this.changeDetectorRef.markForCheck();
    }

    onStartDatetimeChange(startDt: Date): void {
        const schedulingHost = this.host();
        if (!schedulingHost?.dateModel) {
            return;
        }
        // Start-end-datetime also emits endDatetimeChange; apply the full range there to avoid a stale end.
        schedulingHost.dateModel.timeModel.evStartDt = startDt;
    }

    onEndDatetimeChange(endDt: Date): void {
        const schedulingHost = this.host();
        const startDt = schedulingHost?.dateModel?.timeModel.evStartDt ?? undefined;
        if (!schedulingHost?.dateModel || !startDt) {
            return;
        }
        schedulingHost.applyDateTimeRange(startDt, endDt);
        void schedulingHost.onDateTimeChange();
        this.dateTimeRevision.update((revision) => revision + 1);
        this.changeDetectorRef.markForCheck();
    }

    private async initialize(): Promise<void> {
        const schedulingHost = new S25ExpressSchedulingHost(this.initContext(), this.panelApi(), () =>
            this.onHostStateChange(),
        );
        this.host.set(schedulingHost);
        await schedulingHost.bootstrap();
        this.onHostStateChange();
    }

    private onHostStateChange(): void {
        const schedulingHost = this.host();
        if (!schedulingHost) {
            return;
        }
        this.locationSelection.set(schedulingHost.location);
        this.changeDetectorRef.markForCheck();
    }

    onLocationSelectionChange(selectedLocation: Nullable<ExpressSchedulingLocationItem>): void {
        this.locationSelection.set(selectedLocation);
    }

    private async onLocationSelected(selectedLocation: Nullable<ExpressSchedulingLocationItem>): Promise<void> {
        const schedulingHost = this.host();
        if (!schedulingHost) {
            return;
        }
        const location = selectedLocation ?? null;
        this.locationSelection.set(location);
        schedulingHost.location = location;
        await schedulingHost.onSelectLocation(true);
        this.changeDetectorRef.markForCheck();
    }

    saveExpressEvent(): void {
        void this.runWithInlineLoading(async () => {
            await this.syncLocationBeforeSave();
            await this.host()?.createExpressEvent();
        });
    }

    private async syncLocationBeforeSave(): Promise<void> {
        const schedulingHost = this.host();
        if (!schedulingHost?.location && this.locationSelection()) {
            await this.onLocationSelected(this.locationSelection());
        }
    }

    openInForm(): void {
        this.host()?.openInForm();
    }

    exitPostSavePage(): void {
        this.host()?.exitPostSavePage();
    }

    private async runWithInlineLoading(action: () => Promise<unknown> | undefined): Promise<void> {
        this.inlineLoading.set(true);
        try {
            await action();
        } finally {
            this.inlineLoading.set(false);
            this.changeDetectorRef.markForCheck();
        }
    }
}
